
import {computed, defineComponent, reactive, toRefs} from 'vue'
import {numberFormatter} from "@/service/NumberFormatter";
import {InvoiceStatus} from "@/enum/InvoiceStatus";
import {DealStatus} from "@/enum/DealStatus";
import {useDropzone} from "vue3-dropzone";
import APIClient from "@/service/APIClient";

const api = new APIClient();

const CARD_LIKE_OPTIONS = ['CARD_ACQUIRING', 'MANUAL_ECOM', 'CARD_ACQUIRING_REDIRECT'];

export default defineComponent({
  props: ['invoice', 'currentDeal', 'waitingForRequisites'],
  setup(props) {
    const state = reactive({
      disputeFile: null as any,
    });

    const receiptPdfRequired = computed(() => props.invoice.flowData?.options?.receiptPdfRequired === true);
    const hideUploadReceipt = computed(() => receiptPdfRequired.value || props.invoice.flowData?.options?.hideUploadReceipt === true);

    const isAcquiringPayment = computed(() => {
      const option = props.invoice?.paymentOption ?? props.currentDeal?.paymentOption;
      return option != null && CARD_LIKE_OPTIONS.includes(option);
    });

    const isPaymentCanceled = computed(() => {
      if (props.invoice.status === InvoiceStatus.CANCELED) {
        return true;
      }
      if (!isAcquiringPayment.value) {
        return false;
      }
      if (props.currentDeal?.status === DealStatus.CANCELED) {
        return true;
      }
      return props.invoice.deals?.some((deal: any) => 
        CARD_LIKE_OPTIONS.includes(deal.paymentOption) && deal.status === DealStatus.CANCELED
      ) ?? false;
    });

    const isPaymentCompleted = computed(() => {
      if (!isAcquiringPayment.value) {
        return props.invoice.status === InvoiceStatus.PAID;
      }
      if (props.currentDeal?.status === DealStatus.COMPLETED) {
        return true;
      }
      return props.invoice.deals?.some((deal: any) => 
        CARD_LIKE_OPTIONS.includes(deal.paymentOption) && deal.status === DealStatus.COMPLETED
      ) ?? false;
    });

    const isAcquiringWaitingForFunds = computed(() => {
      if (!isAcquiringPayment.value) {
        return false;
      }
      const isWaiting = (option: string | undefined, status: string | undefined): boolean => {
        if (status === DealStatus.TRANSFER_CONFIRMED) {
          return true;
        }
        return option === 'CARD_ACQUIRING_REDIRECT' && status === DealStatus.TRANSFER_WAITING;
      };
      if (isWaiting(props.currentDeal?.paymentOption ?? props.invoice?.paymentOption, props.currentDeal?.status)) {
        return true;
      }
      return props.invoice.deals?.some((deal: any) =>
        isWaiting(deal.paymentOption, deal.status)
      ) ?? false;
    });

    const onDrop = async (acceptFiles: any, rejectReasons: any) => {
      for (const file of acceptFiles) {
        await api.confirmTransfer(props.invoice.id, props.currentDeal.id, file);
        state.disputeFile = file
        return;
      }
    }

    const dropzone = useDropzone({ onDrop, accept:['.pdf', '.jpg', '.jpeg', '.png'], maxFiles: 1 });

    return {
      ...toRefs(state),
      numberFormatter,
      dropzone,
      InvoiceStatus,
      DealStatus,
      isAcquiringPayment,
      isPaymentCanceled,
      isPaymentCompleted,
      isAcquiringWaitingForFunds,
      hideUploadReceipt,
      receiptPdfRequired
    }
  }
})
